import clsx from 'clsx';
import { twMerge } from 'tailwind-merge';

import { GatsbyImg, Link } from 'components';

import { ILearnPageSetQueryAndView } from 'typescriptDefs/pages/learn';
import { IArticleSanityData } from 'typescriptDefs/pages/learn/article';

import { Col, Row } from './GridV2';
import { Body, Heading4 } from './Typography';
import BlockContent from './sanity/BlockContent';

export const ArticleCard = ({
  article,
  className,
  setQueryAndView,
  isLandingPage = false,
  ...props
}: {
  article: IArticleSanityData;
  className?: string;
  isLandingPage?: boolean;
  setQueryAndView?: ILearnPageSetQueryAndView;
}) => (
  <div {...props}>
    <Link
      to={`/${article.subfolder ?? 'learn'}/${article.slug.current}/`}
      className="block cursor-pointer font-bold text-blue-alt no-underline hover:text-blue-deep hover:no-underline focus:no-underline active:no-underline"
    >
      <GatsbyImg
        className="mb-16 !hidden h-auto w-auto xs:!block xs:h-383 xs:w-508 sm:h-384 sm:w-688 md:h-191 md:w-287 lg:h-232 lg:w-348"
        image={article.img.asset.gatsbyImageData}
      />
      <Heading4 as="h3" className="font-bold">
        {article.title}
      </Heading4>
    </Link>
    <Body className="mb-0">{`${article.minutesToRead} mins`}</Body>
    <Link
      className="mb-16 inline-block cursor-pointer font-bold text-blue-alt no-underline hover:text-blue-deep hover:no-underline focus:no-underline active:no-underline"
      localize={false}
      to={`${article.authorData.locale === 'fr' ? '/fr' : ''}/author/${article.authorData.slug.current}/`}
    >
      {article.authorData.name}
    </Link>
    <BlockContent blocks={article._rawExcerpt} noLinks />
  </div>
);

const ArticleCardLink = ({ article, className }: { article: IArticleSanityData; className?: string }): JSX.Element => {
  return (
    <Link
      to={`/${article.subfolder ?? 'learn'}/${article.slug.current}/`}
      className={twMerge(clsx('cursor-pointer', className))}
    >
      <Heading4 as="h3" className="font-bold text-blue-alt transition-colors duration-[0.3s]">
        {article.title}
      </Heading4>
      <Body className="mb-0">{`${article.minutesToRead} mins`}</Body>
      <Body className="font-bold text-blue-alt transition-colors duration-[0.3s]">{article.authorData.name}</Body>
      <BlockContent blocks={article._rawExcerpt} noLinks />
    </Link>
  );
};

export const ArticleCards = ({
  articles,
  className,
  isLandingPage,
  setQueryAndView,
}: {
  articles: IArticleSanityData[];
  className?: string;
  colWidth?: string | number | any[] | undefined;
  isLandingPage?: boolean;
  setQueryAndView?: ILearnPageSetQueryAndView;
}) => (
  <Row className={twMerge(clsx('-mx-16 flex-wrap', className))}>
    {articles.map((article, i) => (
      <Col
        className={twMerge(
          clsx(
            'w-full flex-none px-16 md:w-1/3 [&:not(:last-of-type)>*]:pb-20 xs:[&:not(:last-of-type)>*]:pb-0 [&:not(:last-of-type)]:mb-20 xs:[&:not(:last-of-type)]:mb-40',
            '[&:not(:last-of-type)>*]:border-b-[3px] [&:not(:last-of-type)>*]:border-b-blue-light xs:[&:not(:last-of-type)>*]:border-none',
          ),
        )}
        key={i}
      >
        <ArticleCard
          article={article}
          setQueryAndView={setQueryAndView}
          className="hidden xs:block"
          isLandingPage={isLandingPage && isLandingPage}
        />
        <ArticleCardLink article={article} className="block xs:hidden" />
      </Col>
    ))}
  </Row>
);
